import { CompanyGigShift, GigType } from "model/Gig";
import type { SelectedWorker } from "../modal/gigFormModal.types";

export type OverlappingItem = {
  worker: SelectedWorker;
  shifts: CompanyGigShift[];
};

type OverlapInput = {
  companyId: number;
  description: string;
  selectedWorkers: SelectedWorker[];
  shifts: CompanyGigShift[];
  getOverlappingGig: (
    companyId: number,
    data: {
      workerId: number;
      workGroupId?: number;
      description: string;
      shifts: CompanyGigShift[];
      type: GigType;
    },
  ) => Promise<{ data?: { shifts?: CompanyGigShift[] } }>;
};

/**
 * Checks selected workers for overlapping SOLO shifts and returns only workers with overlaps.
 * Returns an empty list when there are no shifts to check or no selected workers.
 */
export const resolveOverlappingShifts = async (
  input: OverlapInput,
): Promise<OverlappingItem[]> => {
  if (!input.shifts.length || input.selectedWorkers.length === 0) {
    return [];
  }

  const overlapChecks = input.selectedWorkers.map(async (worker) => {
    const payload = {
      workerId: worker.workerId ?? worker.id,
      workGroupId: worker.selectedTeamId,
      description: input.description,
      shifts: input.shifts,
      // Overlap validation is intentionally only enforced for SOLO in create flow.
      type: GigType.SOLO,
    };

    try {
      const response = await input.getOverlappingGig(input.companyId, payload);
      return {
        worker,
        shifts: response.data?.shifts ?? [],
      };
    } catch (_error) {
      return {
        worker,
        shifts: [],
      };
    }
  });

  const overlapResults = await Promise.all(overlapChecks);
  return overlapResults.filter((result) => result.shifts.length > 0);
};

/**
 * Returns only newly added shifts that do not yet have a persisted id.
 */
export const getNewShifts = (shifts: CompanyGigShift[]): CompanyGigShift[] => {
  return shifts.filter((shift) => shift.id === null || shift.id === undefined);
};
